import { Controller } from "stimulus"

/**
 * URL Param Modal — auto-open a modal when the page lands with a specific
 * URL query param. Generic and reusable: not tied to coupons, banners, or
 * any specific markup. Plug it into any modal whose visibility is driven by
 * a CSS class and a URL flag.
 *
 * Values (no defaults: this Stimulus version errors on `{ default: ... }`):
 *   - param           URL query param name to inspect (e.g. "modal").
 *   - value           Expected param value (e.g. "true"). If left empty in the
 *                     HTML, ANY non-null value of `param` triggers the modal.
 *   - visibleClass    CSS class added/removed on the modal target to show/hide
 *                     it (e.g. "coupon-banner__modal--visible").
 *   - lockScrollClass Optional CSS class added/removed on <body> while the
 *                     modal is open (e.g. "noscroll"). If omitted, the
 *                     controller does not touch the body — keeps the
 *                     controller decoupled from project-specific CSS.
 *
 * Targets:
 *   - modal        The element that toggles `visibleClass`.
 *
 * Behaviour:
 *   - On connect: if the current URL contains `param` (and matches `value`
 *     when configured), the modal opens.
 *   - On close: removes the class, closes on Esc / backdrop / × button, and
 *     strips `param` from the URL via history.replaceState so a reload
 *     doesn't re-open the modal and other query params (utm_*, etc.) stay.
 *
 * Sample usage:
 *   <div data-controller="url-param-modal"
 *        data-url-param-modal-param-value="modal"
 *        data-url-param-modal-value-value="true"
 *        data-url-param-modal-visible-class-value="coupon-banner__modal--visible">
 *     <a href="...">trigger</a>
 *     <div data-url-param-modal-target="modal" aria-hidden="true">
 *       <div data-action="click->url-param-modal#close"></div>
 *       <button data-action="click->url-param-modal#close">×</button>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = [ "modal" ]
  static values  = {
    param:           String,
    value:           String,
    visibleClass:    String,
    lockScrollClass: String
  }

  connect() {
    // Bind once so add/removeEventListener share the same reference.
    this.boundOnKeydown = this.onKeydown.bind(this)

    // Track open state so repeated open()/close() calls are idempotent
    // and we don't add/remove the body class or keydown listener twice.
    this.isOpen = false

    if (this.shouldAutoOpen()) {
      this.open()
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundOnKeydown)
  }

  // -----------------------------------------------------------------------
  // Modal lifecycle
  // -----------------------------------------------------------------------
  open() {
    if (!this.hasModalTarget || !this.visibleClassValue) return
    if (this.isOpen) return

    this.modalTarget.classList.add(this.visibleClassValue)
    this.modalTarget.setAttribute("aria-hidden", "false")
    if (this.lockScrollClassValue) {
      document.body.classList.add(this.lockScrollClassValue)
    }
    document.addEventListener("keydown", this.boundOnKeydown)
    this.isOpen = true
  }

  close() {
    if (!this.hasModalTarget || !this.visibleClassValue) return
    if (!this.isOpen) return

    this.modalTarget.classList.remove(this.visibleClassValue)
    this.modalTarget.setAttribute("aria-hidden", "true")
    if (this.lockScrollClassValue) {
      document.body.classList.remove(this.lockScrollClassValue)
    }
    document.removeEventListener("keydown", this.boundOnKeydown)
    this.isOpen = false
    this.stripParamFromUrl()
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------
  // True when the URL carries `param` and (if configured) it equals `value`.
  // When `value` is left empty in the HTML, any non-null value is accepted.
  shouldAutoOpen() {
    if (!this.paramValue) return false

    const actual = new URLSearchParams(window.location.search).get(this.paramValue)
    if (actual === null) return false

    // hasValueValue distinguishes "attribute absent" from "attribute = ''".
    // The user opts into strict matching by setting the attribute.
    return this.hasValueValue ? actual === this.valueValue : true
  }

  // Drop the trigger param from the URL so a reload doesn't re-open the
  // modal and the address bar stays clean. Other params and the hash are
  // preserved.
  stripParamFromUrl() {
    if (!this.paramValue) return

    const url = new URL(window.location.href)
    if (!url.searchParams.has(this.paramValue)) return

    url.searchParams.delete(this.paramValue)
    const search = url.searchParams.toString()
    const next = url.pathname + (search ? `?${search}` : "") + url.hash
    window.history.replaceState({}, "", next)
  }
}
